import React from 'react';
import { useMutation, useQueryClient } from "react-query";
import pluralize from 'pluralize';

import Modal from 'components/ui/Modals/components/Modal';
import api from 'api';
import { FETCH_RUNPAYROLL_DATA } from '../utils/queries';
import { useModal } from 'components/ui/Modals';
import { StandardButton, StandardSecondaryButton } from 'components/ui/Button';
import { SkipTypes } from '../utils/types';
import { format } from 'date-fns';
import { showToastViaEvent } from 'utils/ToastEvents';
import { SetErrorMessageType } from '../contexts/RunPayrollErrorContext';
import { trackXPayrollEvent } from 'utils/analytics';

import styles from './index.module.scss';
import { RunPayrollModalHeader } from './components/RunPayrollModalHeader';

const getSkipHeaderContent = (payrollMonth: string, skipLength: number, type: string, exceptLength?: number|null) => {
  let data = {
    header: "",
    content: <></>
  };

  const exceptCase = Number.isInteger(exceptLength);

  switch(true) {
    case type === SkipTypes.SKIPPED && !exceptCase :
      data = {
        header: "Skip Selected",
        content: <>Payroll for<strong>{` ${skipLength} ${pluralize('employee', skipLength)}`}</strong>{` will be skipped from ${payrollMonth}.`}</>,
      }
      break;
    case type === SkipTypes.SKIPPED && exceptCase :
      data = {
        header: "Skip all except selected",
        content: <>Payroll for<strong>{` ${skipLength} ${pluralize('employee', skipLength)} except selected (${exceptLength})`}</strong>{` will be skipped from ${payrollMonth}.`}</>,
      }
      break;
    case type === SkipTypes.RESUME && !exceptCase :
      data = {
        header: "Resume Selected",
        content: <>Payroll for<strong>{` ${skipLength} ${pluralize('employee', skipLength)}`}</strong>{` will be resumed from ${payrollMonth}.`}</>,
      }
      break;
    case type === SkipTypes.RESUME && exceptCase :
      data = {
        header: "Resume all except selected",
        content: <>Payroll for<strong>{` ${skipLength} ${pluralize('employee', skipLength)} except selected (${exceptLength})`}</strong>{` will be resumed from ${payrollMonth}.`}</>,
      }
      break;
  }

  return data;
}

const SkipPayroll = ({
  payrollMonth,
  skipIds,
  exceptIds,
  affectedCount,
  exceptSelected,
  type,
  onError,
  onSuccess
}: {
  payrollMonth: string;
  skipIds?: number[];
  exceptIds?: number[];
  affectedCount: number;
  // Number of SELECTED (spared) employees, shown as "except selected (N)" in the
  // header. Passed explicitly because the legacy (hrisCustomFields OFF) except
  // flow sends the unselected rows as skipIds, so it cannot be derived from
  // exceptIds. Kept for parity with the pre-except-mode component.
  exceptSelected?: number | null;
  type: string;
  onSuccess: () => void;
} & SetErrorMessageType) => {

  const { closeModal } = useModal();
  const queryClient = useQueryClient();

  const isExceptMode = Array.isArray(exceptIds);

  const skip = useMutation(
    (payrollMonth: string) => {
      if(type === SkipTypes.SKIPPED) {
        return isExceptMode
          ? api.runPayroll.bulkSkipAllExcept(payrollMonth, exceptIds as number[])
          : api.runPayroll.bulkSkipPayroll(payrollMonth, skipIds ?? []);
      } else {
        return isExceptMode
          ? api.runPayroll.bulkResumeAllExcept(payrollMonth, exceptIds as number[])
          : api.runPayroll.bulkResumePayroll(payrollMonth, skipIds ?? []);
      }
    },
    {
      onSuccess: () => {
        showToastViaEvent({
          text: `${affectedCount} ${pluralize('employee', affectedCount)} ${type == SkipTypes.RESUME ? "pay ":""}${pluralize('has', affectedCount)} been ${type == SkipTypes.SKIPPED? "skipped":"resumed"} from ${format(new Date(payrollMonth+ "T00:00:00"), 'MMMM yyyy')} payroll`,
          timeout: 5000,
          type: 'success',
          icon: 'success',
        });
        queryClient.invalidateQueries(FETCH_RUNPAYROLL_DATA);
        onSuccess();
        closeModal();
      },
      onError: (error) => {
        onError && onError({ message: (error as Error).message });
        closeModal();
      },
    },
  );

  const onSkipPayroll = () => {
    trackXPayrollEvent(`runpayroll.${type === SkipTypes.SKIPPED ? 'skipped' : 'resumed'}`);
    skip.mutate(payrollMonth);
  };

  const displayData = getSkipHeaderContent(
    format(new Date(payrollMonth+ "T00:00:00"), 'MMMM yyyy'),
    affectedCount,
    type,
    exceptSelected ?? (isExceptMode ? (exceptIds as number[]).length : null),
  );

  return (
    <Modal>
      <div className={`flex flex-col max-w-xl ${styles['run-payroll-modal']}`}>
        <RunPayrollModalHeader>{displayData.header}</RunPayrollModalHeader>
        <div className={`${styles['run-payroll-modal-content']}`}>
          {displayData.content}
        </div>
        <div className={`flex justify-end ${styles['run-payroll-modal-footer']} gap-4`}>
          <StandardButton onClick={onSkipPayroll} isPending={skip.isLoading}>
            {type === SkipTypes.SKIPPED ? 'SKIP NOW' : 'RESUME PAY'}
          </StandardButton>
          <StandardSecondaryButton 
            onClick={() => closeModal()}
            disabled={skip.isLoading}>
            CANCEL
          </StandardSecondaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default SkipPayroll;
